import React, { useState, useEffect, useMemo } from 'react';
import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	TextField,
	Button,
	Typography,
	Stack,
	Grid2,
	Tabs,
	Tab,
	Checkbox,
	InputAdornment,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from "@mui/material";
import { Iconify } from "../Minimal/src/components/iconify";
import { TablePaginationCustom } from "../Minimal/src/components/pagination";
import { useTable } from "../Minimal/src/hooks/use-table";
import Header from '../Header';
import Footer from '../Footer';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import AccountService from '../../services/AccountServices';
import CommonDatePicker from '../datepicker/CommonDatePicker';
import { API_URL, BASE_URL } from '../Config';
import config from '../common/Config';
import { toast } from 'sonner';

const PaymentProcess = () => {
	const [reportrange, setReportrange] = useState('');
	const [dates, setDates] = useState([null, null]);
	const [draw, setDraw] = useState(1);
	const [start, setStart] = useState(0);
	const [length, setLength] = useState(5000);
	const accessToken = localStorage.getItem('accessToken');
	const user_id = localStorage.getItem('userId');

	const [receivedPayment, setReceivedPayment] = useState([]);
	const [paymentHistory, setPaymentHistory] = useState([]);

	const [phReportrange, setPhReportrange] = useState('');
	const [paymentDates, setPaymentDates] = useState([null, null]);

	const [shouldLoadPayments, setShouldLoadPayments] = useState(false);
	const [isInitialLoad, setIsInitialLoad] = useState(true); // Track initial load

	const [checkedValues, setCheckedValues] = useState([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [phSearchQuery, setPhSearchQuery] = useState("");

	const receivedPaymentTable = useTable({
		defaultRowsPerPage: 50,
		defaultDense: false,
		defaultOrder: "desc",
		defaultOrderBy: "date",
		defaultSelected: [],
		defaultCurrentPage: 0,
	});

	const paymentHistoryTable = useTable({
		defaultRowsPerPage: 50,
		defaultDense: false,
		defaultOrder: "desc",
		defaultOrderBy: "date",
		defaultSelected: [],
		defaultCurrentPage: 0,
	});

	const searchFormData = () => {
		let startDate = dates[0] ? format(dates[0], 'MM/dd/yyyy') : '';
		let endDate = dates[1] ? format(dates[1], 'MM/dd/yyyy') : '';
		setReportrange(startDate ? startDate + ' - ' + endDate : '');
		setShouldLoadPayments(true);
	};

	const searchPHFormData = () => {
		let startDate = paymentDates[0] ? format(paymentDates[0], 'MM/dd/yyyy') : '';
		let endDate = paymentDates[1] ? format(paymentDates[1], 'MM/dd/yyyy') : '';
		setPhReportrange(startDate ? startDate + ' - ' + endDate : '');
		setShouldLoadPayments(true);
	};




	useEffect(() => {
		if (accessToken === null) {
			window.location.href = `${config.BASE_URL}/login`;
			return;
		}
		if (isInitialLoad) {
			loadReceivedPaymentList();
			loadPaymentHistoryList();
			setIsInitialLoad(false);
		} else if (shouldLoadPayments) {
			loadReceivedPaymentList();
			loadPaymentHistoryList();
			setShouldLoadPayments(false);
		}
	}, [shouldLoadPayments, accessToken, isInitialLoad]);

	const loadReceivedPaymentList = async () => {
		try {
			const data = await AccountService.fetchReceivedPaymentList(draw, start, length, reportrange, accessToken);
			setReceivedPayment(Array.isArray(data) ? data : []);
		} catch (error) {
			toast.error('Failed to load received payment list');
		}
	};

	const loadPaymentHistoryList = async () => {
		try {
			const data = await AccountService.fetchPaymentHistoryList(draw, start, length, phReportrange, accessToken);
			setPaymentHistory(Array.isArray(data) ? data : []);
		} catch (error) {
			toast.error('Failed to load payment history list');
		}
	}

	const handleCheckboxChange = (id, checked) => {
		setCheckedValues(prevState => {
			if (checked) {
				return [...prevState, id.toString()];
			} else {
				return prevState.filter(val => val !== id.toString());
			}
		});
	};

	const filteredReceivedPayment = useMemo(() => {
		if (!searchQuery) return receivedPayment;
		const query = searchQuery.toLowerCase();
		return receivedPayment.filter(item =>
			(item.customer && item.customer.toLowerCase().includes(query)) ||
			(item.route && item.route.toLowerCase().includes(query)) ||
			(item.collected_by && item.collected_by.toLowerCase().includes(query)) ||
			(item.mode && item.mode.toLowerCase().includes(query)) ||
			(item.date && item.date.toLowerCase().includes(query))
		);
	}, [receivedPayment, searchQuery]);

	const filteredPaymentHistory = useMemo(() => {
		if (!phSearchQuery) return paymentHistory;
		const query = phSearchQuery.toLowerCase();
		return paymentHistory.filter(item =>
			(item.name && item.name.toLowerCase().includes(query)) ||
			(item.route && item.route.toLowerCase().includes(query)) ||
			(item.driver && item.driver.toLowerCase().includes(query)) ||
			(item.payment_mode && item.payment_mode.toLowerCase().includes(query)) ||
			(item.date && item.date.toLowerCase().includes(query))
		);
	}, [paymentHistory, phSearchQuery]);

	const paginatedReceivedPayment = useMemo(() => {
		return filteredReceivedPayment.slice(
			receivedPaymentTable.page * receivedPaymentTable.rowsPerPage,
			receivedPaymentTable.page * receivedPaymentTable.rowsPerPage + receivedPaymentTable.rowsPerPage
		);
	}, [filteredReceivedPayment, receivedPaymentTable.page, receivedPaymentTable.rowsPerPage]);

	const paginatedPaymentHistory = useMemo(() => {
		return filteredPaymentHistory.slice(
			paymentHistoryTable.page * paymentHistoryTable.rowsPerPage,
			paymentHistoryTable.page * paymentHistoryTable.rowsPerPage + paymentHistoryTable.rowsPerPage
		);
	}, [filteredPaymentHistory, paymentHistoryTable.page, paymentHistoryTable.rowsPerPage]);


	const [activeTab, setActiveTab] = useState('received');

	const handleTabClick = (tab) => {
		setActiveTab(tab);
	};

	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

	const handelAcceptPayment = async () => {
		if (checkedValues.length > 0) {
			setConfirmDialogOpen(true);
		} else {
			toast.error('Please select payment.');
		}
	};

	const handleConfirmAccept = async () => {
		setConfirmDialogOpen(false);
		const formData = new FormData();
		formData.append('user_id', user_id);
		checkedValues.forEach((value) => {
			formData.append('id[]', value);
		});

		const apiAddOrdersUrl = `${config.API_URL}/payment/assign_to_manager`;
		try {
			const response = await fetch(apiAddOrdersUrl, {
				method: 'POST',
				headers: {
					authorization: `Bearer ${accessToken}`,
				},
				body: formData,
			});

			if (!response.ok) {
				toast.error('Something went wrong, try again.');
				return;
			}

			const data = await response.json();
			if (data.success) {
				toast.success('Payment received successfully.');
				setCheckedValues([]);
				setTimeout(() => {
					window.location.href = `${config.BASE_URL}/account/payments`;
				}, 1500);
			} else {
				toast.error('Something went wrong, try again.');
			}
		} catch (error) {
			toast.error('Error accepting payment. Please try again.');
		}
	};

	return (
		<>
			<Box className="page-container">
				<Paper className="paper-style">
					<Box sx={{ px: 2, py: 1.2 }}>
						<Grid2 container spacing={2} display="flex" justifyContent="space-between" alignItems="center">
							<Grid2 size={{ xs: 12, md: 4 }} sx={{ pb: 0.5 }}>
								<Tabs
									value={activeTab}
									onChange={(e, newValue) => handleTabClick(newValue)}
									className="common-tabs"
								>
									<Tab label="Received Payment" value="received" />
									<Tab label="Payment History" value="history" />
								</Tabs>
							</Grid2>
							{activeTab === "history" && (
								<>
									<Grid2 size={{ xs: 12, md: 4 }}>
										<TextField
											placeholder="Search..."
											size="small"
											fullWidth
											className="input-border"
											value={phSearchQuery}
											onChange={(e) => setPhSearchQuery(e.target.value)}
											slotProps={{
												input: {
													startAdornment: (
														<InputAdornment position="start">
															<Iconify icon="eva:search-outline" width={20} height={20} sx={{ color: "#637381" }} />
														</InputAdornment>
													),
													endAdornment: phSearchQuery && (
														<InputAdornment position="end">
															<IconButton
																size="small"
																onClick={() => setPhSearchQuery("")}
																sx={{ color: "#637381" }}
															>
																<Iconify icon="eva:close-fill" width={18} />
															</IconButton>
														</InputAdornment>
													),
												}
											}}
											sx={{
												"& .MuiOutlinedInput-root": {
													backgroundColor: "#f5f7fa",
													"&:hover": {
														backgroundColor: "#fff"
													},
													"&.Mui-focused": {
														backgroundColor: "#fff"
													}
												}
											}}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 4 }} display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="flex-end" alignItems="center" gap={1}>
										<Box sx={{ width: { xs: '100%', sm: '320px' } }}>
											<CommonDatePicker fullWidth className="input-border" value={paymentDates} onChange={setPaymentDates} />
										</Box>
										<Button
											variant="contained"
											onClick={searchPHFormData}
											className="button-variant-contained"
											sx={{ width: { xs: '100%', md: 'auto' } }}
										>
											Submit
										</Button>
									</Grid2>
								</>
							)}
						</Grid2>
					</Box>
					<Box>
						{activeTab === "received" && (
							<>
								<Grid2 container spacing={2} display="flex" justifyContent="space-between" alignItems="center" px={2} pb={1.2}>
									<Grid2 size={{ xs: 12, md: 4 }}>
										<TextField
											placeholder="Search..."
											size="small"
											className="input-border"
											fullWidth
											value={searchQuery}
											onChange={(e) => setSearchQuery(e.target.value)}
											slotProps={{
												input: {
													startAdornment: (
														<InputAdornment position="start">
															<Iconify icon="eva:search-outline" width={20} height={20} sx={{ color: "#637381" }} />
														</InputAdornment>
													),
													endAdornment: searchQuery && (
														<InputAdornment position="end">
															<IconButton
																size="small"
																onClick={() => setSearchQuery("")}
																sx={{ color: "#637381" }}
															>
																<Iconify icon="eva:close-fill" width={18} />
															</IconButton>
														</InputAdornment>
													),
												}
											}}
											sx={{
												"& .MuiOutlinedInput-root": {
													backgroundColor: "#f5f7fa",
													"&:hover": {
														backgroundColor: "#fff"
													},
													"&.Mui-focused": {
														backgroundColor: "#fff"
													}
												}
											}}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 8 }} display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="flex-end" alignItems="center" gap={1}>
										<Box >
											<CommonDatePicker sx={{ width: { xs: '100%', md: '300px' } }} className="input-border" value={dates} onChange={setDates} />
										</Box>
										<Button
											variant="contained"
											onClick={searchFormData}
								
											className="button-variant-contained"
											sx={{ width: { xs: '100%', md: 'auto' } }}
										>
											Submit
										</Button>
										<Button
											variant="contained"
											onClick={handelAcceptPayment}
											className="button-variant-contained"
										
											sx={{ width: { xs: '100%', md: 'auto' } }}
										>
											Accept Payment
										</Button>
									</Grid2>
								</Grid2>
								<TableContainer>
									<Table
										size="medium"
										className="table-cell-border"
										sx={{
											tableLayout: "fixed",
											"& .MuiTableCell-paddingCheckbox": {
												width: 52,
												minWidth: 52,
												px: 0.4,
												py: 0.4,
											}
										}}
									>
										<TableHead>
											<TableRow className="table-cell-header">
												<TableCell padding="checkbox"></TableCell>
												<TableCell width="150px" minWidth="150px">Date</TableCell>
												<TableCell width="300px" minWidth="300px">Customer Name(A/C No)</TableCell>
												<TableCell>Route</TableCell>
												<TableCell>Collected By</TableCell>
												<TableCell>Mode</TableCell>
												<TableCell sx={{ textAlign: "right" }}>Collected Amt</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{filteredReceivedPayment.length === 0 ? (
												<TableRow>
													<TableCell colSpan={7} sx={{ textAlign: "center", py: 8 }}>
														<Typography sx={{ color: "#637381", fontSize: "0.75rem" }}>
															No data available
														</Typography>
													</TableCell>
												</TableRow>
											) : (
												paginatedReceivedPayment.map((row) => {
													const isChecked = checkedValues.includes(row.id?.toString());
													return (
														<TableRow
															key={row.id}
															hover
															className="table-cell-style"
														>
															<TableCell padding="checkbox">
																<Checkbox
																	checked={isChecked}
																	onChange={(e) => handleCheckboxChange(row.id, e.target.checked)}
																	size="small"
																	className="checkBox"
																/>
															</TableCell>
															<TableCell >{row.date || ""}</TableCell>
															<TableCell >{row.customer || ""}</TableCell>
															<TableCell >{row.route || ""}</TableCell>
															<TableCell >{row.collected_by || ""}</TableCell>
															<TableCell >{row.mode || ""}</TableCell>
															<TableCell align="right">
																{row.amount ? parseFloat(row.amount).toFixed(2) : "0.00"}
															</TableCell>
														</TableRow>
													);
												})
											)}
										</TableBody>
									</Table>
								</TableContainer>
								<Box display="flex" justifyContent="flex-end" alignItems="center" pt={2}>
									<TablePaginationCustom
										count={filteredReceivedPayment.length}
										page={receivedPaymentTable.page}
										rowsPerPage={receivedPaymentTable.rowsPerPage}
										onPageChange={receivedPaymentTable.onChangePage}
										onRowsPerPageChange={receivedPaymentTable.onChangeRowsPerPage}
										labelDisplayedRows={({ from, to, count }) => `${from}-${to}${count !== -1 ? ` of ${count}` : ''}`}
										className="pagination-styles"
									/>
								</Box>
							</>
						)}

						{activeTab === "history" && (
							<>
								<TableContainer>
									<Table
										size="medium"
										className="table-cell-border"
										sx={{
											tableLayout: "fixed",
										}}
									>
										<TableHead>
											<TableRow className="table-cell-header">
												<TableCell sx={{width: "150px", minWidth: "150px"}}>Date</TableCell>
												<TableCell sx={{ width: "350px", minWidth: "350px" }}>Customer Name(A/C No)</TableCell>
												<TableCell >Route</TableCell>
												<TableCell>Collected By</TableCell>
												<TableCell>Mode</TableCell>
												<TableCell sx={{ textAlign: "right" }}>Collected Amt</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{filteredPaymentHistory.length === 0 ? (
												<TableRow>
													<TableCell colSpan={6} sx={{ textAlign: "center", py: 8 }}>
														<Typography sx={{ color: "#637381", fontSize: "0.75rem" }}>
															No data available
														</Typography>
													</TableCell>
												</TableRow>
											) : (
												paginatedPaymentHistory.map((row) => (
													<TableRow
														key={row.id || row.date}
														hover
														className="table-cell-style"
														
													>
														<TableCell >{row.date || ""}</TableCell>
														<TableCell >{row.name || ""}</TableCell>
														<TableCell >{row.route || ""}</TableCell>
														<TableCell >{row.driver || ""}</TableCell>
														<TableCell >{row.payment_mode || ""}</TableCell>
														<TableCell sx={{ textAlign: "right" }}>
															{row.amount ? parseFloat(row.amount).toFixed(2) : "0.00"}
														</TableCell>
													</TableRow>
												))
											)}
										</TableBody>
									</Table>
								</TableContainer>
								<Box display="flex" justifyContent="flex-end" alignItems="center" pt={2}>
									<TablePaginationCustom
										count={filteredPaymentHistory.length}
										page={paymentHistoryTable.page}
										rowsPerPage={paymentHistoryTable.rowsPerPage}
										onPageChange={paymentHistoryTable.onChangePage}
										onRowsPerPageChange={paymentHistoryTable.onChangeRowsPerPage}
										labelDisplayedRows={({ from, to, count }) => `${from}-${to}${count !== -1 ? ` of ${count}` : ''}`}
										className="pagination-styles"
									/>
								</Box>
							</>
						)}
					</Box>
				</Paper>
			</Box>
			<Dialog
				open={confirmDialogOpen}
				onClose={() => setConfirmDialogOpen(false)}
				PaperProps={{
					sx: {
						borderRadius: 2,
					},
				}}
			>
				<DialogTitle sx={{ fontWeight: 600, fontSize: "1rem", color: "#212b36" }}>
					Confirm Payment
				</DialogTitle>
				<DialogContent>
					<Typography sx={{ fontSize: "0.875rem", color: "#637381" }}>
						Are you sure you want to accept the selected payments?
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button
						onClick={() => setConfirmDialogOpen(false)}
						variant="outlined"
						sx={{
							borderColor: "#F8991D",
							textTransform: "none",
							fontWeight: 500,
							"&:hover": {
								backgroundColor: "#F8991D",
								color: "#fff",
							},
						}}
					>
						Cancel
					</Button>
					<Button
						onClick={handleConfirmAccept}
						variant="contained"
						className="button-variant-contained"
					>
						Yes, Accept
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
export default PaymentProcess;

